/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { cn } from '@/utils/css'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

/** The carousel context props. */
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  carouselThumbsRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  selectedIndex: number
  thumbnailOnClick: (index: number) => void
} & CarouselProps

/** The carousel context. */
const CarouselContext = React.createContext<CarouselContextProps | null>(null)

/**
 * A hook to use the carousel context.
 */
function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

/**
 * A carousel component that uses Embla under the hood. Originally from the `shadcn` project. Modified to fit the design
 * for the MTa Kit gallery, with additional support for thumbnails.
 */
const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [carouselThumbsRef, thumbsApi] = useEmblaCarousel({
      containScroll: 'keepSnaps',
      dragFree: true,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    })

    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)

    useEffect(() => {
      if (!api || !thumbsApi) {
        return
      }

      thumbsApi.reInit({
        axis: orientation === 'horizontal' ? 'x' : 'y',
      })
      api.reInit({
        axis: orientation === 'horizontal' ? 'x' : 'y',
      })
    }, [orientation, thumbsApi, api])

    /**
     * When a thumbnail is clicked, scroll to the corresponding slide.
     */
    const onThumbClick = useCallback(
      (index: number) => {
        if (!api || !thumbsApi) return
        api.scrollTo(index)
      },
      [api, thumbsApi],
    )

    /**
     * When a slide is selected, scroll the thumbnails to the corresponding slide.
     */
    const onSelect = React.useCallback(
      (api: CarouselApi) => {
        if (!api || !thumbsApi) {
          return
        }

        setCanScrollPrev(api.canScrollPrev())
        setCanScrollNext(api.canScrollNext())

        setSelectedIndex(api.selectedScrollSnap())
        thumbsApi.scrollTo(api.selectedScrollSnap())
      },
      [api, thumbsApi, setSelectedIndex],
    )

    /**
     * Scroll to the previous slide.
     */
    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    /**
     * Scroll to the next slide.
     */
    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    /**
     * Handle the arrow keys for navigating the carousel.
     */
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          carouselThumbsRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          thumbnailOnClick: onThumbClick,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role={'region'}
          aria-roledescription={'carousel'}
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

export { Carousel, useCarousel }
export type { CarouselApi }
